import { Box, Link, Typography } from '@mui/material';
import { useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import remarkGfm from 'remark-gfm';

import { normalizeHelpArticleMarkdown } from '../../lib/helpArticleMarkdown';
import { AuthMedia } from '../AuthMedia';

type MarkdownBodyProps = {
  markdown: string;
  onLinkClick?: (href: string) => boolean;
};

const sanitizeSchema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    img: [...(defaultSchema.attributes?.img ?? []), 'height', 'width'],
  },
};

export function MarkdownBody({ markdown, onLinkClick }: MarkdownBodyProps) {
  const normalizedMarkdown = useMemo(
    () => normalizeHelpArticleMarkdown(markdown),
    [markdown],
  );

  if (!normalizedMarkdown.trim()) {
    return null;
  }

  return (
    <Box
      sx={{
        color: 'text.secondary',
        fontSize: '0.84rem',
        lineHeight: 1.65,
        '& h1, & h2, & h3, & h4': {
          color: 'text.primary',
          fontWeight: 900,
          lineHeight: 1.25,
          mt: 1.6,
          mb: 0.75,
        },
        '& h1': { fontSize: '1.15rem' },
        '& h2': { fontSize: '1rem' },
        '& h3': { fontSize: '0.92rem' },
        '& p, & ul, & ol, & pre, & blockquote': { my: 1 },
        '& ul, & ol': { pl: 2.2 },
        '& li': { mb: 0.35 },
        '& a': { color: 'primary.light' },
        '& img': {
          display: 'block',
          maxWidth: '100%',
          height: 'auto',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
          my: 1.2,
        },
        '& pre': {
          overflowX: 'auto',
          p: 1.2,
          borderRadius: 1,
          bgcolor: 'rgba(255,255,255,0.03)',
          border: '1px solid',
          borderColor: 'divider',
        },
        '& code': {
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '0.78rem',
        },
        '& blockquote': {
          pl: 1.2,
          borderLeft: '3px solid',
          borderColor: 'divider',
          color: 'text.secondary',
        },
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw, [rehypeSanitize, sanitizeSchema]]}
        components={{
          img: ({ alt, src }) => (
            <AuthMedia
              alt={alt ?? ''}
              src={typeof src === 'string' ? src : undefined}
              sx={{
                display: 'block',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                my: 1.2,
              }}
            />
          ),
          a: ({ href, children }) => (
            <Link
              href={href}
              target="_blank"
              rel="noreferrer"
              onClick={(event) => {
                if (href && onLinkClick?.(href)) event.preventDefault();
              }}
            >
              {children}
            </Link>
          ),
          p: ({ children }) => (
            <Typography component="p" sx={{ fontSize: 'inherit', lineHeight: 'inherit', color: 'inherit' }}>
              {children}
            </Typography>
          ),
        }}
      >
        {normalizedMarkdown}
      </ReactMarkdown>
    </Box>
  );
}
